import { Field } from "@base-ui/react/field";
import { useId, useRef, useState, type ComponentProps, type DragEvent, type ReactNode } from "react";
import { buttonClass } from "./button.jsx";
import { cx } from "./cx.js";
import { MarkdownFileIcon, XIcon } from "./icons.jsx";

interface CommonProps {
  name: string;
  label: ReactNode;
  /** Helper text under the control. */
  description?: ReactNode;
  /** Server-side or custom error message. Marks the field invalid. */
  error?: string | null | undefined;
  className?: string;
}

/** A bare text input, for layouts that place the label themselves (settings rows). */
export function Input({ className, ...props }: ComponentProps<"input">) {
  return <input className={cx("dt-input", className)} {...props} />;
}

export interface TextFieldProps extends CommonProps, Omit<ComponentProps<typeof Field.Control>, "name" | "className"> {}

/** Labelled text input built on Base UI Field. Native constraint errors surface automatically. */
export function TextField({ name, label, description, error, className, ...control }: TextFieldProps) {
  return (
    <Field.Root name={name} invalid={!!error} className={cx("dt-field", className)}>
      <Field.Label className="dt-field__label">{label}</Field.Label>
      <Field.Control className="dt-input" {...control} />
      {description && <Field.Description className="dt-field__desc">{description}</Field.Description>}
      <Field.Error className="dt-field__error" />
      {error && (
        <p className="dt-field__error" role="alert">
          {error}
        </p>
      )}
    </Field.Root>
  );
}

export interface TextAreaFieldProps extends CommonProps, Omit<ComponentProps<"textarea">, "name" | "className"> {}

export function TextAreaField({ name, label, description, error, className, rows = 4, ...textarea }: TextAreaFieldProps) {
  return (
    <Field.Root name={name} invalid={!!error} className={cx("dt-field", className)}>
      <Field.Label className="dt-field__label">{label}</Field.Label>
      <Field.Control className="dt-input dt-input--textarea" render={<textarea rows={rows} {...textarea} />} />
      {description && <Field.Description className="dt-field__desc">{description}</Field.Description>}
      <Field.Error className="dt-field__error" />
      {error && (
        <p className="dt-field__error" role="alert">
          {error}
        </p>
      )}
    </Field.Root>
  );
}

export interface FileFieldProps extends CommonProps {
  accept?: string;
  multiple?: boolean;
  /** Names of the files currently held by the caller, in the order they were picked. */
  selected: readonly string[];
  onSelect: (files: File[]) => void;
  onClear?: () => void;
  /** Caller-supplied copy: this package holds no strings of its own. */
  actionLabel: ReactNode;
  /** Sits beside the button — e.g. "No files chosen" or "3 files". */
  status: ReactNode;
  clearLabel?: string;
}

/**
 * File picker in the same visual language as the other controls: the native
 * input stays in the accessibility tree but is visually replaced by a labelled
 * button, and the picked files are listed instead of being summarised as a count.
 * Files can also be dropped onto the control.
 */
export function FileField({ name, label, description, error, className, accept, multiple, selected, onSelect, onClear, actionLabel, status, clearLabel }: FileFieldProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const accepted = (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (!accept) return files;
    // Mirror what the native picker would allow, so a drop cannot smuggle in
    // a type the button would have rejected.
    const patterns = accept.split(",").map((p) => p.trim().toLowerCase());
    return files.filter((f) => patterns.some((p) => (p.startsWith(".") ? f.name.toLowerCase().endsWith(p) : f.type.toLowerCase() === p)));
  };

  const drop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    const files = accepted(e.dataTransfer.files);
    if (files.length > 0) onSelect(multiple ? files : files.slice(0, 1));
  };

  return (
    <Field.Root name={name} invalid={!!error} className={cx("dt-field", className)}>
      <Field.Label className="dt-field__label" htmlFor={id}>
        {label}
      </Field.Label>
      <div
        className={cx("dt-filefield", over && "dt-filefield--over")}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
      >
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="file"
          className="dt-filefield__input"
          {...(accept ? { accept } : {})}
          multiple={multiple ?? false}
          onChange={(e) => onSelect(Array.from(e.currentTarget.files ?? []))}
        />
        <label htmlFor={id} className={buttonClass({ variant: "secondary", size: "sm" })}>
          {actionLabel}
        </label>
        <span className="dt-filefield__status">{status}</span>
        {selected.length > 0 && onClear && (
          <button
            type="button"
            className="dt-filefield__clear"
            aria-label={clearLabel ?? "Clear"}
            onClick={() => {
              if (inputRef.current) inputRef.current.value = "";
              onClear();
            }}
          >
            <XIcon />
          </button>
        )}
      </div>
      {/* The hint explains the control, so it stays next to it; the picked files follow. */}
      {description && <Field.Description className="dt-field__desc">{description}</Field.Description>}
      {selected.length > 0 && (
        <ul className="dt-filefield__list">
          {selected.map((file) => (
            <li key={file}>
              <MarkdownFileIcon />
              <span className="dt-filefield__name">{file}</span>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="dt-field__error" role="alert">
          {error}
        </p>
      )}
    </Field.Root>
  );
}

/** Wraps any control (e.g. a Select) with a label in the same layout as TextField. */
export function FieldGroup({ name, label, description, error, className, children }: CommonProps & { children: ReactNode }) {
  return (
    <Field.Root name={name} invalid={!!error} className={cx("dt-field", className)}>
      <Field.Label className="dt-field__label">{label}</Field.Label>
      {children}
      {description && <Field.Description className="dt-field__desc">{description}</Field.Description>}
      {error && (
        <p className="dt-field__error" role="alert">
          {error}
        </p>
      )}
    </Field.Root>
  );
}
